import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AchievementConfiguration = () => {
  const [achievements, setAchievements] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState(null);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    // Mock data
    const mockAchievements = [
      {
        id: 1,
        name: 'Early Bird',
        description: 'Check in before 7 AM for 5 consecutive days',
        category: 'attendance',
        points: 100,
        rarity: 'common',
        icon: 'Sunrise',
        trigger: 'check_in_time',
        criteria: { time: '07:00', consecutive_days: 5 },
        active: true,
        unlockCount: 234
      },
      {
        id: 2,
        name: 'Workout Warrior',
        description: 'Complete 50 workout sessions',
        category: 'workout',
        points: 250,
        rarity: 'rare',
        icon: 'Dumbbell',
        trigger: 'workout_count',
        criteria: { count: 50 },
        active: true,
        unlockCount: 156
      }
    ];
    setAchievements(mockAchievements);
  };

  const categoryOptions = [
    { value: 'attendance', label: 'Attendance' },
    { value: 'workout', label: 'Workouts' },
    { value: 'classes', label: 'Classes' },
    { value: 'referral', label: 'Referrals' },
    { value: 'community', label: 'Community' }
  ];

  const rarityOptions = [
    { value: 'common', label: 'Common' },
    { value: 'uncommon', label: 'Uncommon' },
    { value: 'rare', label: 'Rare' },
    { value: 'epic', label: 'Epic' },
    { value: 'legendary', label: 'Legendary' }
  ];

  const iconOptions = [
    { value: 'Award', label: 'Award' },
    { value: 'Trophy', label: 'Trophy' },
    { value: 'Star', label: 'Star' },
    { value: 'Flame', label: 'Flame' },
    { value: 'Zap', label: 'Zap' },
    { value: 'Heart', label: 'Heart' },
    { value: 'Target', label: 'Target' }
  ];

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
              Achievement Configuration
            </h2>
            <p className="text-sm text-muted-foreground">
              Create and manage custom achievement badges
            </p>
          </div>
          <Button iconName="Plus" onClick={() => setShowCreateModal(true)}>
            Create Achievement
          </Button>
        </div>

        <div className="space-y-3">
          {achievements?.map((achievement) => (
            <div
              key={achievement?.id}
              className="p-4 rounded-lg border border-border hover:border-border/60 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name={achievement?.icon} size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">
                        {achievement?.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {achievement?.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => setEditingAchievement(achievement)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={achievement?.active ? 'Eye' : 'EyeOff'}
                      >
                        {achievement?.active ? 'Active' : 'Inactive'}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="px-2 py-1 rounded-full bg-primary/20 text-primary font-medium capitalize">
                      {achievement?.category}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-accent/20 text-accent font-medium capitalize">
                      {achievement?.rarity}
                    </span>
                    <span className="text-muted-foreground">
                      <Icon name="Coins" size={12} className="inline mr-1" />
                      {achievement?.points} points
                    </span>
                    <span className="text-muted-foreground">
                      <Icon name="Users" size={12} className="inline mr-1" />
                      {achievement?.unlockCount} unlocks
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Badge Designer Preview */}
      <div className="card">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Visual Badge Designer
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Badge Preview</h3>
            <div className="p-8 rounded-lg border-2 border-dashed border-border bg-muted/30 flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                  <Icon name="Award" size={48} color="var(--color-primary)" />
                </div>
                <p className="text-sm font-medium text-foreground">Achievement Badge</p>
                <p className="text-xs text-muted-foreground">Preview will appear here</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <Select
              label="Badge Icon"
              options={iconOptions}
              placeholder="Select icon"
            />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Background Color" type="color" defaultValue="#3b82f6" />
              <Input label="Icon Color" type="color" defaultValue="#ffffff" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementConfiguration;